import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { CHAINS, getExplorerAddressUrl } from '../../services/api';
import { Icons } from './DashboardLayout';

const WALLET_TYPES = {
  SMART_ACCOUNT: 'Smart Account',
  EOA: 'EOA Wallet',
  GNOSIS_SAFE: 'Gnosis Safe',
};

const SUPPORTED_CHAINS = ['base', 'ethereum', 'arbitrum', 'optimism', 'polygon'];

export default function WalletsPage() {
  const { agents, token, refreshAgents } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterChain, setFilterChain] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadWallets();
  }, [agents, token]);

  const loadWallets = async () => {
    setLoading(true);
    try {
      // Collect wallets from all agents
      const allWallets = [];
      for (const agent of agents) {
        if (agent.wallets) {
          agent.wallets.forEach(wallet => {
            allWallets.push({
              ...wallet,
              agent_id: agent.id,
              agent_name: agent.name,
            });
          });
        }
      }
      
      // Fetch balances for each wallet
      const walletsWithBalances = await Promise.all(
        allWallets.map(async (wallet) => {
          try {
            const balanceRes = await api.getWalletBalance(token, wallet.address, wallet.chain);
            return {
              ...wallet,
              balance: balanceRes.eth || { balance: '0', formatted: '0' },
              tokens: balanceRes.tokens || []
            };
          } catch {
            return { ...wallet, balance: { balance: '0', formatted: '0' }, tokens: [] };
          }
        })
      );
      
      setWallets(walletsWithBalances);
    } catch (err) {
      console.error('Failed to load wallets:', err);
    }
    setLoading(false);
  };

  const handleWalletAdded = () => {
    refreshAgents();
    loadWallets();
  };

  // Get unique chains from wallets
  const chains = [...new Set(wallets.map(w => w.chain))];

  // Filter wallets
  const filteredWallets = wallets.filter(w => {
    if (filterAgent !== 'all' && w.agent_id !== filterAgent) return false;
    if (filterChain !== 'all' && w.chain !== filterChain) return false;
    return true;
  });

  // Calculate total balance
  const totalBalanceUsd = filteredWallets.reduce((sum, w) => {
    const ethBalance = parseFloat(w.balance?.formatted || 0);
    // Rough ETH price estimate - in real app, fetch from API
    return sum + (ethBalance * 2500);
  }, 0);

  if (wallets.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-6">
          <Icons.Wallets size={32} className="text-white/30" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">No Wallets Yet</h3>
        <p className="text-white/50 text-center max-w-md mb-6">
          Create an agent to automatically generate a smart account wallet, or add wallets on other chains.
        </p>
        {agents.length > 0 && (
          <motion.button
            onClick={() => setShowAddModal(true)}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="add-wallet-empty-btn"
          >
            + Add Wallet
          </motion.button>
        )}
        
        {/* Add Wallet Modal */}
        <AnimatePresence>
          {showAddModal && (
            <AddWalletModal
              agents={agents}
              wallets={wallets}
              token={token}
              onClose={() => setShowAddModal(false)}
              onSuccess={() => {
                setShowAddModal(false);
                handleWalletAdded();
              }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      <div className="flex items-center gap-8">
        <div>
          <p className="text-white/40 text-[10px] uppercase tracking-wider">Total Balance</p>
          <p className="text-2xl font-bold text-white">${totalBalanceUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div>
          <p className="text-white/40 text-[10px] uppercase tracking-wider">Wallets</p>
          <p className="text-2xl font-bold text-white">{filteredWallets.length}</p>
        </div>

        {/* Filters & Add Button */}
        <div className="ml-auto flex items-center gap-3">
          <select
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm outline-none"
          >
            <option value="all">All Agents</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <select
            value={filterChain}
            onChange={(e) => setFilterChain(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm outline-none"
          >
            <option value="all">All Chains</option>
            {chains.map(c => (
              <option key={c} value={c}>{CHAINS[c]?.name || c}</option>
            ))}
          </select>
          <motion.button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black font-medium text-sm"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="add-wallet-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Wallet
          </motion.button>
        </div>
      </div>

      {/* Wallets Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-[200px] rounded-2xl bg-white/[0.02] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredWallets.map((wallet, index) => (
            <WalletCard key={`${wallet.address}-${wallet.chain}`} wallet={wallet} index={index} />
          ))}
        </div>
      )}

      {/* Add Wallet Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddWalletModal
            agents={agents}
            wallets={wallets}
            token={token}
            onClose={() => setShowAddModal(false)}
            onSuccess={() => {
              setShowAddModal(false);
              handleWalletAdded();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function WalletCard({ wallet, index }) {
  const chain = CHAINS[wallet.chain] || { name: wallet.chain, logo: null };
  const [copied, setCopied] = useState(false);
  const [copiedSigner, setCopiedSigner] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(wallet.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopySigner = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(wallet.turnkeyAddress);
    setCopiedSigner(true);
    setTimeout(() => setCopiedSigner(false), 2000);
  };

  const truncatedAddress = `${wallet.address.slice(0, 8)}...${wallet.address.slice(-6)}`;
  const truncatedSignerAddress = wallet.turnkeyAddress 
    ? `${wallet.turnkeyAddress.slice(0, 8)}...${wallet.turnkeyAddress.slice(-6)}`
    : null;
  const ethBalance = parseFloat(wallet.balance?.formatted || 0);
  const usdBalance = ethBalance * 2500; // Rough estimate

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group relative p-5 rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/[0.06] hover:border-white/[0.12] transition-all overflow-hidden"
    >
      {/* Hover glow */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.03) 0%, transparent 60%)' }}
      />
      
      {/* Header */}
      <div className="relative flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-black/40 border border-white/[0.08] flex items-center justify-center overflow-hidden">
            {chain.logo ? (
              <img src={chain.logo} alt={chain.name} className="w-6 h-6 object-contain" />
            ) : (
              <span className="text-white/50 text-sm font-medium">{chain.name?.slice(0, 2)}</span>
            )}
          </div>
          <div>
            <p className="text-white font-medium">{chain.name}</p>
            <p className="text-white/40 text-xs">{WALLET_TYPES[wallet.type] || wallet.type}</p>
          </div>
        </div>
      </div>

      {/* Smart Account Address */}
      <div className="mb-3">
        <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Smart Account (Main Wallet)</p>
        <div 
          className="relative p-3 rounded-xl bg-black/50 border border-white/[0.04] cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <code className="text-xs text-white/60 font-mono">
              {expanded ? wallet.address : truncatedAddress}
            </code>
            <div className="flex items-center gap-1">
              <button
                onClick={handleCopy}
                className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-all"
              >
                {copied ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <Icons.Copy size={14} />
                )}
              </button>
              <a
                href={getExplorerAddressUrl(wallet.chain, wallet.address)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-all"
              >
                <Icons.External size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Turnkey EOA Signer Address */}
      {wallet.turnkeyAddress && (
        <div className="mb-4">
          <p className="text-yellow-400/70 text-[10px] uppercase tracking-wider mb-1.5">Signer EOA (Pays Gas)</p>
          <div className="relative p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
            <div className="flex items-center justify-between">
              <code className="text-xs text-yellow-400/80 font-mono">
                {truncatedSignerAddress}
              </code>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleCopySigner}
                  className="p-1.5 rounded-lg text-yellow-400/50 hover:text-yellow-400 hover:bg-yellow-500/10 transition-all"
                >
                  {copiedSigner ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <Icons.Copy size={14} />
                  )}
                </button>
                <a
                  href={getExplorerAddressUrl(wallet.chain, wallet.turnkeyAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded-lg text-yellow-400/50 hover:text-yellow-400 hover:bg-yellow-500/10 transition-all"
                >
                  <Icons.External size={14} />
                </a>
              </div>
            </div>
            <p className="text-yellow-400/50 text-[10px] mt-1.5">Fund this address with ETH for gas</p>
          </div>
        </div>
      )}

      {/* Balance */}
      <div className="relative flex items-center justify-between pt-4 border-t border-white/[0.06]">
        <div>
          <p className="text-white/40 text-[10px] uppercase tracking-wider">Balance</p>
          <p className="text-white font-bold text-lg">{ethBalance.toFixed(4)} ETH</p>
          <p className="text-white/40 text-xs">${usdBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className="text-right">
          <p className="text-white/40 text-[10px] uppercase tracking-wider">Agent</p>
          <p className="text-white text-sm">{wallet.agent_name}</p>
        </div>
      </div>
    </motion.div>
  );
}


// Add Wallet Modal
function AddWalletModal({ agents, wallets, token, onClose, onSuccess }) {
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedChain, setSelectedChain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get available chains for selected agent (chains not yet added)
  const getAvailableChainsForAgent = (agentId) => {
    const agentWallets = wallets.filter(w => w.agent_id === agentId);
    const usedChains = agentWallets.map(w => w.chain);
    return SUPPORTED_CHAINS.filter(chain => !usedChains.includes(chain));
  };

  const availableChains = selectedAgent ? getAvailableChainsForAgent(selectedAgent) : [];

  // Reset chain selection when agent changes
  const handleAgentChange = (agentId) => {
    setSelectedAgent(agentId);
    setSelectedChain('');
    setError('');
  };

  const handleAddWallet = async () => {
    if (!selectedAgent || !selectedChain) {
      setError('Please select an agent and chain');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await api.addWallet(token, selectedAgent, selectedChain);
      if (res.success) {
        onSuccess();
      } else {
        setError(res.error?.message || 'Failed to add wallet');
      }
    } catch (err) {
      setError('Failed to add wallet. Please try again.');
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Add Wallet</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Info */}
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
            <p className="text-blue-400/80 text-sm">
              Add a wallet on another EVM chain for your agent. The wallet will use the same address across all chains.
            </p>
          </div>

          {/* Agent Selection */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Select Agent</label>
            <select
              value={selectedAgent}
              onChange={(e) => handleAgentChange(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none focus:border-white/20 transition-colors"
              data-testid="add-wallet-agent-select"
            >
              <option value="">Choose an agent...</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>{agent.name}</option>
              ))}
            </select>
          </div>

          {/* Chain Selection */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Select Chain</label>
            {selectedAgent && availableChains.length === 0 ? (
              <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
                <p className="text-yellow-400/80 text-sm">
                  This agent already has wallets on all supported chains.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {(selectedAgent ? availableChains : SUPPORTED_CHAINS).map(chain => (
                  <button
                    key={chain}
                    onClick={() => setSelectedChain(chain)}
                    disabled={!selectedAgent}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      selectedChain === chain
                        ? 'bg-white/10 border-white/20'
                        : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'
                    } ${!selectedAgent ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`chain-option-${chain}`}
                  >
                    {CHAINS[chain]?.logo && (
                      <img src={CHAINS[chain].logo} alt="" className="w-6 h-6 object-contain" />
                    )}
                    <span className="text-white text-sm font-medium">{CHAINS[chain]?.name || chain}</span>
                    {selectedChain === chain && (
                      <svg className="ml-auto w-4 h-4 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button 
            onClick={onClose} 
            className="px-4 py-2 rounded-xl text-white/60 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <motion.button
            onClick={handleAddWallet}
            disabled={loading || !selectedAgent || !selectedChain}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
            data-testid="add-wallet-submit-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                Adding...
              </span>
            ) : (
              'Add Wallet'
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
